import { useEffect } from 'react';
import { useActivityStore } from '../store/useActivityStore';
import { logDebug } from '../services/loggerService';

/**
 * Mirrors the unread activity count onto the installed PWA's app-icon badge.
 *
 * Reads the same `activityCount` that drives the in-app bell, so the icon
 * badge and the bell can never disagree while the app is open. The
 * background half lives in `public/firebase-messaging-sw.js`, which sets
 * the badge from the `badgeCount` field the backend stamps onto every push
 * payload (see `messaging.SendPushNotification`).
 *
 * This foreground half is what makes the badge go DOWN: the backend only
 * pushes on new events, so an app-open → read-notifications flow clears the
 * badge here, not via a push.
 *
 * Platform notes:
 *   - Badging only applies to an INSTALLED PWA. In a normal browser tab the
 *     API is either absent or rejects; both are non-events, hence the guard
 *     and the swallowed rejection.
 *   - iOS 16.4+ supports it for home-screen PWAs with notification
 *     permission granted.
 *   - Chrome on Android renders a dot regardless of the number passed.
 */
export function useAppBadge(): void {
  const activityCount = useActivityStore((s) => s.activityCount);

  useEffect(() => {
    if (!('setAppBadge' in navigator)) return;

    // clearAppBadge() rather than setAppBadge(0): passing 0 is spec'd to
    // clear too, but Safari has historically shown a zero-dot for it.
    const apply =
      activityCount > 0 ? navigator.setAppBadge(activityCount) : navigator.clearAppBadge();

    void apply.catch(() => {
      /* Not installed, or permission absent — badging is a no-op, not an error. */
    });

    logDebug(`App badge set to ${activityCount}`);
  }, [activityCount]);
}
